import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Typography,
    Box,
    Tabs,
    Tab,
    Paper,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Card,
    CardContent,
    Chip,
    TextField
} from '@mui/material';
import {
    Schedule as ScheduleIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Today as TodayIcon,
    CalendarMonth as CalendarIcon,
    EventAvailable as UpcomingIcon
} from '@mui/icons-material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { AppointmentList } from '../../appointments';
import * as appointmentActions from '../../../actions/seller/seller.appointments';
import * as sellerActions from '../../../actions/seller/seller.store';
import UnifiedPullToRefresh from '../../elements/main/UnifiedPullToRefresh';

function TabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`store-appointments-tabpanel-${index}`}
            aria-labelledby={`store-appointments-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

const StoreAppointments = ({ showOnlyPendingAndToday = false }) => {
    const dispatch = useDispatch();
    const [tabValue, setTabValue] = useState(0);
    const [statusFilter, setStatusFilter] = useState('');
    const [dateFilter, setDateFilter] = useState(null);
    
    const {
        storeAppointments = [],
        storeAppointmentsLoading = false,
        storeAppointmentsError = null,
        storeAppointmentsPagination = {
            current_page: 1,
            total_pages: 1,
            total_appointments: 0,
            per_page: 10
        },
        actionSuccess = false
    } = useSelector(state => state.sellerAppointments || {});
    
    const fetchAppointments = useCallback(() => {
        const params = {};
        
        if (statusFilter) {
            params.status = statusFilter;
        }
        
        if (dateFilter) {
            params.date = moment(dateFilter).format('YYYY-MM-DD');
        }
        
        dispatch(appointmentActions.getStoreAppointments(params));
    }, [dispatch, statusFilter, dateFilter]);
    
    // Load appointments when filters change
    useEffect(() => {
        fetchAppointments();
    }, [fetchAppointments]);
    
    useEffect(() => {
        // Refetch appointments when an action is successful (confirm, cancel, etc.)
        if (actionSuccess) {
            fetchAppointments();
            dispatch(appointmentActions.clearAppointmentSuccess());
        }
    }, [actionSuccess, dispatch, fetchAppointments]);
    
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        
        // Update status filter based on tab
        switch (newValue) {
            case 0: // Upcoming (scheduled, confirmed) - future appointments only
                setStatusFilter('scheduled,confirmed');
                setDateFilter(null);
                break;
            case 1: // Pending (scheduled) - all scheduled appointments
                setStatusFilter('scheduled');
                setDateFilter(null);
                break;
            case 2: // All appointments - keep current filters or reset to default
                // Don't automatically reset filters when switching to All tab
                // Let users keep their current filter selections
                if (statusFilter === 'scheduled,confirmed' || statusFilter === 'scheduled') {
                    setStatusFilter('');
                    setDateFilter(null);
                }
                break;
            default:
                setStatusFilter('');
                setDateFilter(null);
        }
    };
    
    const getUpcomingAppointments = () => {
        return (storeAppointments || []).filter(apt => 
            ['scheduled', 'confirmed'].includes(apt.status) &&
            moment(apt.start_time).isAfter(moment())
        );
    };
    
    const getTodayAppointments = () => {
        const today = moment().format('YYYY-MM-DD');
        return (storeAppointments || []).filter(apt => 
            moment(apt.start_time).format('YYYY-MM-DD') === today
        );
    };
    
    const getPendingAppointments = () => {
        return (storeAppointments || []).filter(apt => 
            apt.status === 'scheduled' && 
            moment(apt.start_time).isAfter(moment())
        );
    };
    
    const getConfirmedAppointments = () => {
        return (storeAppointments || []).filter(apt => apt.status === 'confirmed');
    };
    
    const getCancelledAppointments = () => {
        return (storeAppointments || []).filter(apt => apt.status === 'cancelled');
    };
    
    const getPendingAndTodayAppointments = () => {
        const today = moment().format('YYYY-MM-DD');
        return (storeAppointments || []).filter(apt => 
            apt.status === 'scheduled' || 
            moment(apt.start_time).format('YYYY-MM-DD') === today
        );
    };
    
    const getFilteredAppointments = () => {
        const appointments = storeAppointments || [];
        
        switch (tabValue) {
            case 0: // Upcoming
                return getUpcomingAppointments();
            case 1: // Pending
                return getPendingAppointments();
            case 2: // All - use the appointments from API (already filtered by backend)
                return appointments;
            default: // All
                return appointments;
        }
    };
    
    const getStatusCounts = () => {
        const appointments = storeAppointments || [];
        return {
            total: appointments.length,
            upcoming: getUpcomingAppointments().length,
            pending: getPendingAppointments().length,
            confirmed: getConfirmedAppointments().length,
            today: getTodayAppointments().length,
            cancelled: appointments.filter(apt => apt.status === 'cancelled').length,
            completed: appointments.filter(apt => apt.status === 'completed').length
        };
    };
    
    const statusCounts = getStatusCounts();
    
    const handleRefresh = () => {
        fetchAppointments();
    };
    
    return (
        <UnifiedPullToRefresh
            onRefresh={handleRefresh}
            isRefreshing={storeAppointmentsLoading}
            refreshText={{
                pull: 'Pull to refresh appointments',
                release: 'Release to refresh appointments',
                refreshing: 'Refreshing appointments...'
            }}
        >
            <Container maxWidth="lg" sx={{ py: 4 }}>
                    <Box mb={4}>
                        <Typography variant="h6" component="h6" gutterBottom>
                            Video Shopping Appointments
                        </Typography>
                    </Box>
                
                
                <Paper sx={{ width: '100%' }}>
                    {showOnlyPendingAndToday ? (
                        // Simplified view for SellerHome - show only pending and today's appointments
                        <Box sx={{ p: 3 }}>
                            {storeAppointmentsError ? (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {storeAppointmentsError}
                                </Alert>
                            ) : null}
                            
                            {getPendingAndTodayAppointments().length === 0 && !storeAppointmentsLoading ? (
                                <Box textAlign="center" py={4}>
                                    <ScheduleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                                    <Typography variant="h6" color="textSecondary" gutterBottom>
                                        No pending or today's appointments
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        You're all caught up! No appointments need immediate attention.
                                    </Typography>
                                </Box>
                            ) : (
                                <Box>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        Showing pending appointments and today's schedule ({getPendingAndTodayAppointments().length} total)
                                    </Alert>
                                    <AppointmentList
                                        userType="store"
                                        appointments={getPendingAndTodayAppointments()}
                                        loading={storeAppointmentsLoading}
                                        onRefresh={fetchAppointments}
                                        compact={true}
                                        hideFilter={true}
                                    />
                                </Box>
                            )}
                        </Box>
                    ) : (
                        // Full view with tabs
                        <>
                            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                <Tabs value={tabValue} onChange={handleTabChange} aria-label="store appointment tabs">
                                    <Tab 
                                        label={`Upcoming (${statusCounts.upcoming})`} 
                                        icon={<UpcomingIcon />} 
                                        iconPosition="start"
                                    />
                                    <Tab 
                                        label={`Pending (${statusCounts.pending})`} 
                                        icon={<ScheduleIcon />} 
                                        iconPosition="start"
                                    />
                                    <Tab 
                                        label={`All (${statusCounts.total})`} 
                                        icon={<CalendarIcon />} 
                                        iconPosition="start"
                                    />
                                </Tabs>
                            </Box>
                            
                            <TabPanel value={tabValue} index={0}>
                                {storeAppointmentsError ? (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        {storeAppointmentsError}
                                    </Alert>
                                ) : null}
                                
                                {getUpcomingAppointments().length === 0 && !storeAppointmentsLoading ? (
                                    <Box textAlign="center" py={6}>
                                        <UpcomingIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                                        <Typography variant="h6" color="textSecondary" gutterBottom>
                                            No upcoming appointments
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            You have no scheduled or confirmed appointments coming up.
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box>
                                        <Alert severity="info" sx={{ mb: 2 }}>
                                            These are your upcoming appointments (scheduled and confirmed) that are in the future.
                                        </Alert>
                                        <AppointmentList
                                            userType="store"
                                            appointments={getUpcomingAppointments()}
                                            loading={storeAppointmentsLoading}
                                            onRefresh={fetchAppointments}
                                            hideFilter={true}
                                        />
                                    </Box>
                                )}
                            </TabPanel>
                            
                            <TabPanel value={tabValue} index={1}>
                                {getPendingAppointments().length === 0 ? (
                                    <Box textAlign="center" py={6}>
                                        <ScheduleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                                        <Typography variant="h6" color="textSecondary" gutterBottom>
                                            No pending appointments
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            All appointments have been confirmed or there are no new bookings.
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box>
                                        <Alert severity="info" sx={{ mb: 2 }}>
                                            These are future appointments waiting for your confirmation. 
                                            Confirming appointments helps customers know their booking is secured.
                                        </Alert>
                                        <AppointmentList
                                            userType="store"
                                            appointments={getPendingAppointments()}
                                            loading={storeAppointmentsLoading}
                                            onRefresh={fetchAppointments}
                                            hideFilter={true}
                                        />
                                    </Box>
                                )}
                            </TabPanel>
                            
                            <TabPanel value={tabValue} index={2}>
                                {storeAppointmentsError ? (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        {storeAppointmentsError}
                                    </Alert>
                                ) : null}
                                
                                {/* Filters for All tab */}
                                <Grid container spacing={2} mb={3}>
                                    <Grid item xs={12} md={4}>
                                        <FormControl fullWidth>
                                            <InputLabel>Filter by Status</InputLabel>
                                            <Select
                                                value={statusFilter}
                                                onChange={(e) => setStatusFilter(e.target.value)}
                                                label="Filter by Status"
                                            >
                                                <MenuItem value="">All Statuses</MenuItem>
                                                <MenuItem value="scheduled">Scheduled</MenuItem>
                                                <MenuItem value="confirmed">Confirmed</MenuItem>
                                                <MenuItem value="cancelled">Cancelled</MenuItem>
                                                <MenuItem value="completed">Completed</MenuItem>
                                                <MenuItem value="no_show">No Show</MenuItem>
                                                <MenuItem value="missed">Missed</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Box>
                                            <Typography variant="subtitle2" gutterBottom>
                                                Filter by Date
                                            </Typography>
                                            <DatePicker
                                                selected={dateFilter ? dateFilter.toDate() : null}
                                                onChange={(date) => setDateFilter(date ? moment(date) : null)}
                                                dateFormat="MMMM d, yyyy"
                                                customInput={<TextField fullWidth />}
                                                placeholderText="Select a date"
                                                isClearable
                                            />
                                        </Box>
                                    </Grid>
                                </Grid>
                                
                                {(storeAppointments || []).length === 0 && !storeAppointmentsLoading ? (
                                    <Box textAlign="center" py={6}>
                                        <CalendarIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                                        <Typography variant="h6" color="textSecondary" gutterBottom>
                                            No appointments found
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Customers haven't booked any appointments yet.
                                        </Typography>
                                    </Box>
                                ) : (
                                    <AppointmentList
                                        userType="store"
                                        appointments={storeAppointments || []}
                                        loading={storeAppointmentsLoading}
                                        pagination={storeAppointmentsPagination}
                                        onRefresh={fetchAppointments}
                                        hideFilter={true}
                                    />
                                )}
                            </TabPanel>
                        </>
                    )}
                </Paper>
            </Container>
        </UnifiedPullToRefresh>
    );
};

export default StoreAppointments;
